import type { ActorEvent, RecordLikeProtocol, RichTextContentProtocol } from '@lighthouse/core'
import { EVENT_TYPE, type FieldADTValue, VIEW_DESIGN_STYLE } from '@lighthouse/core'
import {
    fieldConvertValue,
    getImageFullUrlInApplication,
    RowHighlighter,
    selectRecordFromPool,
    useActionRunningLoadings,
    useApplicationContext,
    useAtomData
} from '@lighthouse/shared'
import { Checkbox, Image } from '@mantine/core'
import cls from 'classnames'
import React, { memo, useCallback, useDeferredValue, useMemo } from 'react'

import { RecordOperator } from '../../components/RecordOperator'
import { useViewBlockContext } from '../../context'
import { getImageFromField } from '../../utils'
import { ListChecker } from './ListChecker'
import type { ListRecordProps } from './ListRecordStyle'
import {
    SCxDivider,
    SCxEmphasizeText,
    SCxHandle,
    SCxListAvatarWrapper,
    SCxListField,
    SCxListItemsWrapper,
    SCxListItemWrapper,
    SCxListMainItemWrapper,
    SCxListPrimaryItemWrapper,
    SCxListRecord,
    SCxListRecordContainer,
    SCxListRecordContent,
    SCxListSecondaryItem
} from './ListRecordStyle'
import { ListValue } from './ListValue'
import { getDesignStyleMap, getDesignTypeMap } from './utils'

export const ListRecord: React.FC<ListRecordProps> = memo(
    ({
        id,
        index,
        primaryField,
        viewFields,
        isMobile,
        rowStyleConditions,
        selectedRecords,
        checkable,
        hoverColor,
        designConfig,
        eventList,
        recordOperator,
        isLast,
        onRecordSelect,
        onRecordOperatorActionTrigger,
        onRecordClick,
        onRenderButtonTitle
    }) => {
        const { uniqueId, blockData, recordPoolAtom, dataSource } = useViewBlockContext()
        const { id: blockId } = blockData
        const { id: dsId, schema } = dataSource
        // 使用 Map 的 O(1) 查找，useMemo 保持 selector 引用稳定
        const selector = useMemo(() => selectRecordFromPool(dsId, id), [dsId, id])
        const record = useDeferredValue(useAtomData(recordPoolAtom, selector))
        const { id: recordId, content: recordContent } = record || ({ id: '', content: {} } as RecordLikeProtocol)
        const handleSelectRecord = useCallback(
            (checked: boolean, id: string) => {
                if (!selectedRecords) {
                    return
                }
                if (checked) {
                    onRecordSelect?.([...selectedRecords, id])
                } else {
                    onRecordSelect?.(selectedRecords.filter(rId => rId !== id))
                }
            },
            [onRecordSelect, selectedRecords]
        )
        const { appId } = useApplicationContext()

        const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()

        const {
            designStyle,
            designType,
            isShowListAvatar,
            listAvatar = '',
            isShowEmphasizeText,
            emphasizeTextField,
            avatarType
        } = designConfig

        const designStyleOption = useMemo(() => getDesignStyleMap(designStyle, isMobile), [designStyle, isMobile])

        const designTypeOption = useMemo(() => getDesignTypeMap(designType), [designType])

        const primaryFieldCell = recordContent?.[primaryField?.id || '']

        const emphasizeText = useMemo(() => {
            if (isShowEmphasizeText && emphasizeTextField) {
                const text = recordContent?.[emphasizeTextField]?.value
                return typeof text === 'string' || typeof text === 'number' ? text : ''
            }
            return ''
        }, [emphasizeTextField, isShowEmphasizeText, recordContent])

        const fieldList = useMemo(() => {
            let list = viewFields
            if (isShowListAvatar && listAvatar) {
                list = list.filter(item => item.fieldId !== listAvatar)
            }
            if (isShowEmphasizeText && emphasizeTextField) {
                list = list.filter(item => item.fieldId !== emphasizeTextField)
            }
            return list
        }, [emphasizeTextField, isShowEmphasizeText, isShowListAvatar, listAvatar, viewFields])

        const recordClickEvent = useMemo(() => {
            return eventList?.find(ev => ev.type === EVENT_TYPE.RECORD_CLICK)
        }, [eventList])

        const handleRecordClick = useCallback(() => {
            if (!onRecordClick || !record || !recordClickEvent) {
                return
            }
            handleActionTriggerWithLoading({
                id: uniqueId,
                actorEvent: recordClickEvent,
                trigger: (id, actorEvent) => onRecordClick(id, actorEvent, record)
            })
        }, [handleActionTriggerWithLoading, onRecordClick, record, recordClickEvent, uniqueId])

        const handleActionTrigger = useCallback(
            async (id: string, actorEvent: ActorEvent) => {
                await onRecordOperatorActionTrigger?.(id, actorEvent, record)
            },
            [onRecordOperatorActionTrigger, record]
        )

        const handleRenderButtonTitle = useCallback(
            (v: RichTextContentProtocol) => {
                return onRenderButtonTitle(v, record)
            },
            [onRenderButtonTitle, record]
        )

        const primaryFieldIsFull = useMemo(() => {
            return designStyleOption.isPrimaryAlone || fieldList.length === 1
        }, [designStyleOption.isPrimaryAlone, fieldList.length])

        const handleContent = useMemo(() => {
            if (!recordOperator || recordOperator.length === 0) {
                return null
            }
            return (
                <SCxHandle onClick={ev => ev.stopPropagation()}>
                    {!designStyleOption.isNumberOrder && selectedRecords && selectedRecords.length > 0 ? (
                        <Checkbox
                            size="xs"
                            disabled={!checkable}
                            checked={selectedRecords.includes(recordId)}
                            onChange={ev => {
                                if (!checkable) {
                                    return
                                }
                                handleSelectRecord(ev.target.checked, recordId)
                            }}
                        />
                    ) : (
                        <RecordOperator
                            uniqueId={uniqueId}
                            actions={recordOperator}
                            isMobile={isMobile}
                            preShowCount={2}
                            recordId={recordId}
                            record={record}
                            onActionTrigger={handleActionTrigger}
                            onRenderButtonTitle={handleRenderButtonTitle}
                        />
                    )}
                </SCxHandle>
            )
        }, [
            recordOperator,
            designStyleOption.isNumberOrder,
            selectedRecords,
            checkable,
            recordId,
            uniqueId,
            isMobile,
            record,
            handleActionTrigger,
            handleRenderButtonTitle,
            handleSelectRecord
        ])

        if (!record) {
            return null
        }

        return (
            <SCxListRecord style={{ cursor: loadings[recordId] ? 'wait' : 'pointer' }}>
                <SCxListRecordContainer
                    className={cls({ 'record-active': selectedRecords?.includes(recordId) })}
                    openable={!!recordClickEvent || !!onRecordClick}
                    key={recordId}
                    background={`var(${designTypeOption.background})`}
                    hoverColor={hoverColor}
                    data-key={`${blockId}&${recordId}`}
                    data-record-id={recordId}
                    onClick={handleRecordClick}
                >
                    <RowHighlighter viewFields={viewFields} recordContent={recordContent} recordConditions={rowStyleConditions} />
                    {designStyleOption.isNumberOrder && (
                        <ListChecker
                            checkable={checkable}
                            isRecordSelecting={selectedRecords && selectedRecords.length > 0}
                            index={index}
                            checked={selectedRecords?.includes(recordId)}
                            onChange={checked => handleSelectRecord(checked, recordId)}
                        />
                    )}
                    {isShowListAvatar && (
                        <SCxListAvatarWrapper margin={designStyleOption.avatarMargin}>
                            <Image
                                src={getImageFullUrlInApplication(appId, getImageFromField(recordContent[listAvatar]))}
                                radius={avatarType === 'round' ? 30 : 8}
                                width={designStyleOption.imageSize}
                                height={designStyleOption.imageSize}
                                alt="Norway"
                                withPlaceholder
                                placeholder={<div style={{ background: 'var(--color-gray-200)' }} />}
                            />
                        </SCxListAvatarWrapper>
                    )}
                    <SCxListRecordContent direction={designStyleOption.contentDirection}>
                        {designStyleOption.isPrimaryAlone && (
                            <SCxListMainItemWrapper padding={designStyleOption.titlePadding} isFullWidth={primaryFieldIsFull}>
                                {isShowEmphasizeText && emphasizeTextField && (
                                    <SCxEmphasizeText
                                        lineHeight={designStyleOption.emphasizeTextLineHeight}
                                        size={designStyleOption.emphasizeTextSize}
                                        marginBottom={designStyleOption.emphasizeTextLineMarginBottom}
                                    >
                                        {emphasizeText}
                                    </SCxEmphasizeText>
                                )}
                                {primaryField && primaryFieldCell && (
                                    <SCxListPrimaryItemWrapper style={{ padding: '2px 0' }}>
                                        <ListValue
                                            field={primaryField}
                                            isColumn={designStyle === VIEW_DESIGN_STYLE.simple}
                                            isBold={designStyleOption.titleBold}
                                            size={designStyleOption.titleSize}
                                            data={primaryFieldCell.value}
                                        />
                                    </SCxListPrimaryItemWrapper>
                                )}
                            </SCxListMainItemWrapper>
                        )}
                        <SCxListItemsWrapper direction={designStyleOption.wrapperDirection}>
                            {!designStyleOption.isPrimaryAlone && (
                                <SCxListItemWrapper
                                    isFullWidth={primaryFieldIsFull}
                                    isBorderBottom={designStyle !== VIEW_DESIGN_STYLE.simple}
                                >
                                    {isShowEmphasizeText && emphasizeTextField && (
                                        <SCxEmphasizeText
                                            lineHeight={designStyleOption.emphasizeTextLineHeight}
                                            size={designStyleOption.emphasizeTextSize}
                                            marginBottom={designStyleOption.emphasizeTextLineMarginBottom}
                                        >
                                            {emphasizeText}
                                        </SCxEmphasizeText>
                                    )}
                                    {primaryField && primaryFieldCell && (
                                        <SCxListPrimaryItemWrapper>
                                            <ListValue
                                                field={primaryField}
                                                isBold={designStyleOption.titleBold}
                                                size={designStyleOption.titleSize}
                                                data={primaryFieldCell.value}
                                            />
                                        </SCxListPrimaryItemWrapper>
                                    )}
                                </SCxListItemWrapper>
                            )}
                            <SCxListField
                                direction={designStyleOption.listDirection}
                                justify={designStyleOption.listJustify}
                                isWidth={designStyleOption.listIsFullWidth}
                            >
                                {fieldList.map(curField => {
                                    const cId = curField.fieldId
                                    const field = schema[cId]
                                    if (cId === primaryField?.id) {
                                        return null
                                    }
                                    const cellData = recordContent?.[cId]
                                    const fieldValue = {
                                        ...field,
                                        value: cellData?.value
                                    } as FieldADTValue
                                    const dataIsEmpty = !fieldConvertValue(fieldValue)
                                    if (!field || !curField.visible || dataIsEmpty) {
                                        return null
                                    }

                                    return (
                                        <SCxListSecondaryItem
                                            key={cId}
                                            marginBottom={designStyleOption.listItemMarginBottom}
                                            isWidth={designStyleOption.listItemIsFullWidth}
                                        >
                                            <ListValue field={field} data={cellData?.value} isColumn={designStyleOption.listItemIsWidth} />
                                        </SCxListSecondaryItem>
                                    )
                                })}
                            </SCxListField>
                        </SCxListItemsWrapper>
                    </SCxListRecordContent>
                    {handleContent}
                </SCxListRecordContainer>
                {designTypeOption.isDivider && designStyleOption.isDivider && !isLast && (
                    <SCxDivider margin={designTypeOption.dividerMargin} color="var(--color-gray-200)" />
                )}
            </SCxListRecord>
        )
    }
)
